import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, PhoneCall, TrendingDown, PiggyBank, Timer, Home as HomeIcon, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import SectorCard from "@/components/SectorCard";
import AnimatedCounter from "@/components/AnimatedCounter";
import MethodTimeline from "@/components/MethodTimeline";
import CEEExplainer from "@/components/CEEExplainer";
import ExpertiseGrid from "@/components/ExpertiseGrid";
import RealisationsGallery from "@/components/RealisationsGallery";
import ComparisonTable from "@/components/ComparisonTable";
import StatsBand from "@/components/StatsBand";
import TestimonialSlider from "@/components/TestimonialSlider";
import ContactSection from "@/components/ContactSection";
import BlogHighlight from "@/components/BlogHighlight";
import AccompagnementBadge from "@/components/AccompagnementBadge";
import ParticuliersSpotlight from "@/components/ParticuliersSpotlight";
import CEEFinancingExplainer from "@/components/CEEFinancingExplainer";
import FranceCoverageMap from "@/components/FranceCoverageMap";
import MandatairesBanner from "@/components/MandatairesBanner";
import SolutionsHome from "@/components/SolutionsHome";
import HowItWorksSteps from "@/components/HowItWorksSteps";
import IndustrySectorsGrid from "@/components/IndustrySectorsGrid";
import EstimatorWizard from "@/components/EstimatorWizard";
import BeforeAfterSchemas from "@/components/BeforeAfterSchemas";
import AnonymousCaseStudies from "@/components/AnonymousCaseStudies";
import TechLibrary from "@/components/TechLibrary";
import TechFAQ from "@/components/TechFAQ";
import ManufacturerLogos from "@/components/ManufacturerLogos";
import { sectors } from "@/data/sectors";

const heroCounters = [
  { value: 480, suffix: "+", label: "Projets étudiés" },
  { value: 142, suffix: " M€", label: "Travaux financés" },
  { value: 60, suffix: " %", label: "d'économies max." },
  { value: 13, suffix: " régions", label: "Présence nationale" },
];

const IMPACT_PROMISES = [
  { Icon: TrendingDown, value: "Jusqu'à 40 %", label: "d'économies d'énergie", accent: "#10B981" },
  { Icon: PiggyBank, value: "100 %", label: "de vos travaux finançables", accent: "#0055A4" },
  { Icon: Timer, value: "48 h", label: "pour votre étude technique offerte", accent: "#0EA5E9" },
];

export default function Home() {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("loadeddata", tryPlay, { once: true });
    return () => v.removeEventListener("loadeddata", tryPlay);
  }, []);

  return (
    <div data-testid="home-page">
      {/* ───────────────── HERO ───────────────── */}
      <section data-testid="home-hero" className="relative min-h-screen flex items-end overflow-hidden bg-white text-slate-900">
        {/* Fond décoratif MPR-style : gradient subtil bleu-vert */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#F0F7FF] to-[#ECFDF5]" />
        <div className="absolute -top-40 -right-40 h-[620px] w-[620px] rounded-full bg-[#0055A4]/[0.08] blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#10B981]/[0.10] blur-3xl pointer-events-none" />
        {/* Grid pattern subtil */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#0055A4 1px, transparent 1px), linear-gradient(90deg, #0055A4 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-40 md:pt-44 pb-20 md:pb-28 w-full">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.28em] uppercase mb-7 fade-up px-3.5 py-1.5 rounded-full bg-[#0055A4]/[0.08] border border-[#0055A4]/20">
              <Sparkles className="h-3.5 w-3.5 text-[#0055A4]" /> <span className="text-[#0055A4]">Leader français du financement CEE</span>
            </div>

            {/* Mandataires Banner — Logos officiels MPR · Anah · CEE */}
            <div
              data-testid="hero-mandat-baseline"
              className="mb-8 fade-up"
              style={{ animationDelay: "0.03s" }}
            >
              <MandatairesBanner theme="light" />
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-[80px] font-bold tracking-[-0.025em] text-balance leading-[0.98] fade-up text-slate-900" style={{ animationDelay: "0.05s" }}>
              Financez votre transition énergétique
              <span className="block mt-3">
                grâce aux <span className="relative inline-block">
                  <span className="relative z-10 bg-gradient-to-r from-[#0055A4] via-[#0369A1] to-[#10B981] bg-clip-text text-transparent">Certificats d&apos;Économies d&apos;Énergie</span>
                </span>
              </span>
            </h1>
            <p className="mt-8 text-base md:text-xl text-slate-600 max-w-2xl leading-relaxed fade-up" style={{ animationDelay: "0.1s" }}>
              Nous détectons les gisements d&apos;économies d&apos;énergie, réalisons les études techniques,
              et accompagnons le financement de vos projets grâce aux dispositifs CEE.
            </p>

            {/* 3 promesses chiffrées */}
            <div data-testid="hero-impact-promises" className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-3 fade-up" style={{ animationDelay: "0.12s" }}>
              {IMPACT_PROMISES.map(({ Icon, value, label, accent }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200 shadow-sm"
                >
                  <div className="h-10 w-10 rounded-xl grid place-items-center flex-shrink-0" style={{ backgroundColor: `${accent}18`, color: accent }}>
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-display text-lg font-bold text-slate-900 leading-none">{value}</div>
                    <div className="text-[11px] text-slate-500 mt-1 leading-tight">{label}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 fade-up" style={{ animationDelay: "0.12s" }}>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white shadow-sm px-2 py-2" data-testid="hero-audience-toggle">
                <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-500 pl-3 pr-1.5">Vous êtes</span>
                <button
                  data-testid="hero-audience-particulier"
                  onClick={() => {
                    document.querySelector('[data-testid="particuliers-spotlight"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="group inline-flex items-center gap-2 rounded-full bg-[#0055A4] text-white hover:bg-[#003D7A] px-4 py-2.5 text-[12.5px] font-semibold tracking-wide transition"
                >
                  <HomeIcon className="h-4 w-4" />
                  Particulier
                  <span className="text-[10px] text-white/70 hidden sm:inline">· PAC</span>
                  <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                </button>
                <button
                  data-testid="hero-audience-pro"
                  onClick={() => {
                    document.querySelector('[data-testid="sectors-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="group inline-flex items-center gap-2 rounded-full bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-900 px-4 py-2.5 text-[12.5px] font-semibold tracking-wide transition"
                >
                  <Briefcase className="h-4 w-4 text-[#10B981]" />
                  Pro
                  <span className="text-[10px] text-slate-500 hidden sm:inline">· CEE</span>
                  <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                </button>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3 fade-up" style={{ animationDelay: "0.15s" }}>
              <Link to="/eligibilite" data-testid="hero-cta-audit">
                <Button className="bg-[#0055A4] hover:bg-[#003D7A] text-white font-semibold py-7 px-9 rounded-full shadow-[0_20px_50px_-20px_rgba(0,85,164,0.65)] hover:-translate-y-0.5 transition-all text-base group">
                  Demander mon audit
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <Link to="/solutions" data-testid="hero-cta-solutions">
                <Button variant="outline" className="border-2 border-slate-300 bg-white hover:bg-slate-50 text-slate-900 font-semibold py-7 px-9 rounded-full text-base">
                  Découvrir nos solutions
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200 fade-up" style={{ animationDelay: "0.2s" }}>
            {heroCounters.map((s) => (
              <div key={s.label} className="bg-white px-6 py-7">
                <div className="font-display text-3xl md:text-4xl font-bold text-slate-900">
                  <AnimatedCounter value={s.value} suffix={s.suffix || ""} />
                </div>
                <div className="text-[11px] md:text-xs uppercase tracking-[0.22em] text-slate-500 mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-slate-400 text-[10px] uppercase tracking-[0.3em] pointer-events-none">
          <span>Scroll</span>
          <div className="h-12 w-px bg-gradient-to-b from-slate-400 to-transparent" />
        </div>
      </section>

      {/* ───────────────── PARTICULIERS SPOTLIGHT (Mandataire MPR/CEE) — Priorité 1 ───────────────── */}
      <ParticuliersSpotlight />

      {/* ───────────────── SECTORS (3 mondes) — Pros ───────────────── */}
      <section data-testid="sectors-section" className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14 md:mb-20">
            <div className="md:col-span-7">
              <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Nos secteurs Pro</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight text-balance leading-[1.05]">
                Trois mondes. Une expertise transversale.
              </h2>
            </div>
            <div className="md:col-span-5 text-[#475569] md:text-lg leading-relaxed">
              Du procédé industriel le plus exigeant à la copropriété la plus complexe,
              nos ingénieurs identifient les leviers CEE éligibles — et les financent.
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {sectors.map((s, i) => (<SectorCard key={s.id} sector={s} index={i} />))}
          </div>
        </div>
      </section>

      {/* ───────────────── INDUSTRY SECTORS (9 icons) ───────────────── */}
      {/* Supprimé pour épuration */}

      {/* ───────────────── SOLUTIONS TECHNIQUES (8 cards) ───────────────── */}
      {/* Supprimé pour épuration */}

      {/* ───────────────── MANUFACTURER LOGOS ───────────────── */}
      {/* Supprimé pour épuration */}

      {/* ───────────────── BEFORE / AFTER SCHEMAS ───────────────── */}
      {/* Supprimé — schéma technique retiré */}

      {/* ───────────────── HOW IT WORKS (6 étapes) ───────────────── */}
      {/* Supprimé — schéma process retiré */}

      {/* ───────────────── ESTIMATOR WIZARD ───────────────── */}
      {/* Supprimé pour épuration */}

      {/* ───────────────── ANONYMOUS CASE STUDIES ───────────────── */}
      {/* Supprimé pour épuration */}

      {/* ───────────────── TECH LIBRARY ───────────────── */}
      {/* Supprimé — schémas techniques retirés */}

      {/* ───────────────── METHOD ───────────────── */}
      {/* Supprimé — schéma method retiré */}

      {/* ───────────────── CEE EXPLAINER (ancien) ───────────────── */}
      {/* Remplacé par CEEFinancingExplainer institutionnel plus bas */}

      {/* ───────────────── EXPERTISE GRID ───────────────── */}
      {/* Supprimé pour épuration */}

      {/* ───────────────── REALISATIONS ───────────────── */}
      <RealisationsGallery limit={6} />

      {/* ───────────────── COMPARISON ───────────────── */}
      {/* Supprimé — schéma comparatif retiré */}

      {/* ───────────────── STATS ───────────────── */}
      <StatsBand dark />

      {/* ───────────────── TESTIMONIALS ───────────────── */}
      <TestimonialSlider />

      {/* ───────────────── ACCOMPAGNEMENT MPR × ANAH ───────────────── */}
      <section className="bg-white py-20 md:py-24 border-t border-black/5">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <AccompagnementBadge theme="light" />
        </div>
      </section>

      {/* ───────────────── CEE FINANCING EXPLAINER ───────────────── */}
      <CEEFinancingExplainer />

      {/* ───────────────── FRANCE COVERAGE MAP ───────────────── */}
      <FranceCoverageMap />

      {/* ───────────────── BLOG HIGHLIGHT ───────────────── */}
      <BlogHighlight />

      {/* ───────────────── TECH FAQ ───────────────── */}
      {/* Supprimé pour épuration */}

      {/* ───────────────── CTA BAND ───────────────── */}
      <section data-testid="home-cta-band" className="bg-gradient-to-br from-[#F0F7FF] via-white to-[#ECFDF5] text-slate-900 border-t border-slate-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#0055A4] uppercase mb-5">Prêts à passer à l&apos;action</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-balance leading-[1.05] text-slate-900">
              Votre prochain projet, financé par les CEE.
            </h2>
            <p className="mt-5 text-slate-600 md:text-lg max-w-2xl leading-relaxed">
              48 heures pour un premier diagnostic. 6 semaines pour un dossier complet.
              Une équipe d&apos;experts engagée à vos côtés sur le long terme.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <Link to="/eligibilite" data-testid="cta-band-eligibility">
              <Button className="w-full bg-[#0055A4] hover:bg-[#003D7A] text-white font-semibold py-7 rounded-full shadow-[0_20px_50px_-20px_rgba(0,85,164,0.55)] text-base">
                Demander un audit gratuit <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="cta-band-contact">
              <Button variant="outline" className="w-full border-2 border-slate-300 bg-white hover:bg-slate-50 text-slate-900 font-semibold py-7 rounded-full text-base">
                <PhoneCall className="mr-2 h-5 w-5" /> Parler à un expert
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────────── CONTACT ───────────────── */}
      <ContactSection />
    </div>
  );
}
